import type { Product, ProductForm } from '../../services/productService';

/**
 * ตรรกะฟอร์มสินค้า (ไม่มี UI) — ย้ายจากโมดัลใน ProductPage (28 ต.ค. 2026) ให้หน้าดู (ProductViewPage)
 * และหน้าเพิ่ม/แก้ไข (ProductFormPage) ใช้ชุดเดียว
 */

/**
 * GET /products/ (รายการ) คืน camelCase แต่ GET/PUT /products/{id} คืน snake_case (ProductSchema)
 * → หน้ารายละเอียด/แก้ไขที่โหลดรายตัวต้องอ่านได้ทั้งสองแบบ ไม่งั้นช่องค่าเช่า/มัดจำ/สถานะว่าง
 */
export const productFromApi = (raw: any): Product => {
  if (!raw || typeof raw !== 'object') return raw;
  const pick = (camel: string, snake: string) => (raw[camel] !== undefined ? raw[camel] : raw[snake]);
  const num = (v: unknown, fallback: number) => {
    const n = Number(v);
    return v === null || v === undefined || v === '' || !Number.isFinite(n) ? fallback : n;
  };
  return {
    ...raw,
    description: raw.description ?? '',
    monthlyRentalRate: num(pick('monthlyRentalRate', 'monthly_rental_rate'), 0),
    depositAmount: num(pick('depositAmount', 'deposit_amount'), 0),
    minimumRentalDays: num(pick('minimumRentalDays', 'minimum_rental_days'), 1),
    unit: raw.unit ?? '',
    isActive: Boolean(pick('isActive', 'is_active') ?? true),
    syncedFromFlow: Boolean(pick('syncedFromFlow', 'synced_from_flow') ?? false),
    flowaccountId: pick('flowaccountId', 'flowaccount_id') ?? undefined,
    flowaccountSyncDate: pick('flowaccountSyncDate', 'flowaccount_sync_date') ?? undefined,
    created_at: raw.created_at ?? raw.createdAt,
    updated_at: raw.updated_at ?? raw.updatedAt,
  } as Product;
};

/** ฟอร์มเปล่าตอน "เพิ่มสินค้าใหม่" — ค่าเดียวกับ handleAddProduct ของโมดัลเดิม
 *  (description/unit ไม่ใส่ = service ไม่ส่งค่า เหมือนเดิม) */
export const emptyProductForm = (code = ''): ProductForm => ({
  code,
  name: '',
  monthlyRentalRate: 0,
  depositAmount: 0,
  minimumRentalDays: 30,
  isActive: true,
});

/** สินค้า → ค่าในฟอร์ม · ใช้ทั้งหน้าดูและหน้าแก้ไข (ชุดช่องเดียวกับ handleEditProduct ของโมดัลเดิม) */
export const productToForm = (product: Product): ProductForm => ({
  code: product.code || '',
  name: product.name || '',
  description: product.description ?? '',
  monthlyRentalRate: product.monthlyRentalRate,
  depositAmount: product.depositAmount,
  minimumRentalDays: product.minimumRentalDays,
  unit: product.unit ?? '',
  isActive: product.isActive,
});

/** ตรวจฟอร์มฝั่ง FE — คืน {} เมื่อผ่าน · key = name ของช่อง */
export const validateProductForm = (formData: ProductForm): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!formData.code.trim()) errors.code = 'กรุณาใส่รหัสสินค้า';
  if (!formData.name.trim()) errors.name = 'กรุณาใส่ชื่อสินค้า';
  // FB-2026-0014: ค่าเช่าไม่บังคับ (ราคาจริงต่างกันต่อโครงการ เซลล์เสนอเอง) — กันแค่ติดลบ/ไม่ใช่ตัวเลข
  const rate = Number(formData.monthlyRentalRate ?? 0);
  if (!Number.isFinite(rate) || rate < 0) {
    errors.monthlyRentalRate = 'ค่าเช่ารายเดือนต้องเป็น 0 หรือมากกว่า';
  }
  return errors;
};

/** token If-Match (P-S468-1) — updated_at ล่าสุดที่โหลดมา (ยังไม่เคยแก้ = created_at) */
export const productIfMatch = (product: Product): string | undefined =>
  (product as any).updated_at || (product as any).created_at || undefined;

/** ชื่อฟิลด์ฝั่ง backend → ป้ายไทย (ไทม์ไลน์ "ประวัติกิจกรรม") */
export const PRODUCT_FIELD_LABELS: Record<string, string> = {
  code: 'รหัสสินค้า',
  name: 'ชื่อสินค้า',
  description: 'รายละเอียด',
  monthly_rental_rate: 'ค่าเช่ารายเดือน',
  deposit_amount: 'เงินมัดจำ',
  minimum_rental_days: 'จำนวนวันเช่าขั้นต่ำ',
  unit: 'หน่วย',
  is_active: 'สถานะ',
};

export const formatBaht = (n: unknown): string => {
  const v = Number(n);
  return Number.isFinite(v)
    ? `${v.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท`
    : '-';
};
